import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { IDynamicDialogConfig } from '@app/shared/modules/dynamic-dialog/model';
import { SubscriberComponent } from '@app/shared/modules/subscriber/base/subscriber.component.base';
import { AmplitudeService } from '@app/shared/services/amplitude.service';
import { CustomerContext } from '@app/shared/services/context/customer.context.service';
import { ModalService } from '@app/shared/services/modal.service';
import { ACTION_EVENT_PROPERTY, ACTION_NAME, AMPLITUDE_EVENT } from '@app/shared/types/enums/amplitude';
import { catchError, EMPTY, filter, switchMap, takeUntil, tap } from 'rxjs';
import { Certificates } from '../../../../../types/enums/certificates.enum';
import { ContractContext } from '@app/shared/services/context/contract.context.service';
import { IContract, IDataCertificates } from '@app/shared/models/contract.model';
import { b64toBlob } from '@app/shared/utils/convert-b64-blob';
import FileSaver from 'file-saver';
import { CardBasicService } from '@app/shared/services/wallet/card-basic.service';
import { trigger, state, transition, animate, style } from '@angular/animations';
import { Years } from '@app/shared/mappers/years.mapper';

@Component({
  selector: 'app-contract-certifications',
  templateUrl: './contract-certifications.component.html',
  styleUrls: ['./contract-certifications.component.sass'],
  animations: [trigger('fadeInOut', [state('void', style({ opacity: 0 })), transition('void <=> *', animate('0.5s ease-in-out'))])],
})
export class ContractCertificationsComponent extends SubscriberComponent implements OnInit, OnDestroy {
  initialData!: IDynamicDialogConfig;
  certificateForm!: FormGroup;
  contract: IContract = {} as IContract;
  isLoading = false;
  certificates: any[];
  typesCertificate: any[] | undefined;
  showYearField = false;
  showCertificationType = false;
  showRactYearField = false;
  years: number[] = [];
  ractYears: number[] = [];
  availableCertificates: IDataCertificates[] | undefined;

  constructor(
    private readonly modalSvc: ModalService,
    public readonly _contractContextService: ContractContext,
    private readonly _amplitudeSvc: AmplitudeService,
    private readonly _customerCtxSvc: CustomerContext,
    private readonly _cardBasicSvc: CardBasicService
  ) {
    super();
    this.contract = this._contractContextService.getCurrentContract() || ({} as IContract);

    this.certificates = [
      {
        title: 'MODAL_CERTIFICATIONS.CONTRACT.PRODUCT.TITLE',
        description: 'MODAL_CERTIFICATIONS.CONTRACT.PRODUCT.DESCRIPTION',
        value: Certificates.CONTRACT_WITH_BALANCE,
        disabled: this.contract.state !== 'ACTIVE',
      },
      {
        title: 'MODAL_CERTIFICATIONS.CONTRACT.PRODUCT_WITHOUT_BALANCE.TITLE',
        description: 'MODAL_CERTIFICATIONS.CONTRACT.PRODUCT_WITHOUT_BALANCE.DESCRIPTION',
        value: Certificates.ACTIVE_CONTRACT,
        disabled: this.contract.state !== 'ACTIVE',
      },
      {
        title: 'MODAL_CERTIFICATIONS.CONTRACT.CANCEL_PRODUCT.TITLE',
        description: 'MODAL_CERTIFICATIONS.CONTRACT.CANCEL_PRODUCT.DESCRIPTION',
        value: Certificates.GOOD_STANDING,
        disabled: this.contract.state === 'ACTIVE',
      },
      {
        title: 'MODAL_CERTIFICATIONS.CONTRACT.TAX_CERTIFICATE.TITLE',
        description: 'MODAL_CERTIFICATIONS.CONTRACT.TAX_CERTIFICATE.DESCRIPTION',
        value: Certificates.TAX_CERTIFICATE,
        disabled: false,
      },
      {
        title: 'MODAL_CERTIFICATIONS.CONTRACT.RACT.TITLE',
        description: 'MODAL_CERTIFICATIONS.CONTRACT.RACT.DESCRIPTION',
        value: Certificates.RACT,
        disabled: false,
      },
    ];
  }

  ngOnInit() {
    this.getmodalData();
    this.removeCurrentYear();
    this.initForm();
    this.logInitEvent();
    this.setupSubscriptions();
    this.certificateAvailable();
    this.loadRactYears();
  }

  certificateAvailable() {
    this._subscriptions.sink = this._cardBasicSvc.getTaxCertificateAvailable(this.contract.rpp_user_id, this.contract.contract_number)
          .subscribe({
            next: (available: IDataCertificates[]) => {
              if(available && available.length > 0) {
                this.availableCertificates = available;
                this.years = available.map(item => item.year);
              }
            }
          });
  }

  loadRactYears() {
    this._subscriptions.sink = this._cardBasicSvc.getRactYears(this.contract.rpp_user_id, this.contract.contract_number)
      .subscribe({
        next: (years: number[]) => {
          if (years && years.length > 0) {
            const currentYear = new Date().getFullYear();
            this.ractYears = years.filter(year => year !== currentYear).sort((a, b) => b - a);
          } else {
            this.ractYears = [];
          }
        },
        error: () => {
          this.ractYears = [];
        }
      });
  }

  removeCurrentYear() {
    this.years = this.years.filter(year => year !== new Date().getFullYear());
  }

  private logInitEvent(): void {
    this._amplitudeSvc.logEvent<ACTION_EVENT_PROPERTY>(AMPLITUDE_EVENT.ACTION_INIT, {
      actionName: ACTION_NAME.CONTRACT_CERTIFICATES,
    });
  }

  isButtonDisabled(choice: any): boolean {
    if (choice.value === Certificates.TAX_CERTIFICATE) {
      return this.years.length === 0;
    }

    if (choice.value === Certificates.RACT) {
      return this.ractYears.length === 0;
    }

    return choice.disabled;
  }

  private setupSubscriptions(): void {
    this.modalSvc.isPrimaryActionClicked$
      .pipe(
        takeUntil(this.destroy$),
        filter(value => value),
        switchMap(() => {
          const user = this._customerCtxSvc.context;
          const certificate = this.certificateForm.get('certificate')?.value;
          const year = this.certificateForm.get('year')?.value || null;

          // Si es RACT, usar el endpoint específico
          if (certificate === Certificates.RACT) {
            return this._cardBasicSvc.downloadRactCertificate(
              user?.rppUserId || '',
              this.contract.contract_number,
              year,
              user?.email || ''
            ).pipe(
              tap(res => {
                const file = b64toBlob(res?.file || '', 'application/pdf');
                FileSaver.saveAs(file, res?.name);
              }),
              catchError(() => {
                this.modalSvc.closeWithErrorMsg(this.initialData.modalComponentData.modalName);

                return EMPTY;
              })
            );
          }

          // Para otros certificados, usar el método existente
          const code = this.certificateForm.get('year')?.value ? this.contract.contract_number : this.contract.code;
          const typeCertificate = this.certificateForm.get('typeCertificate')?.value || null;
          const contractCodeHistory = this.certificateForm.get('year')?.value ? this.contract.contract_code : undefined;

          return this._cardBasicSvc.migrationCertificateDD(user?.rppUserId || '', code, certificate, year, typeCertificate, contractCodeHistory).pipe(
            tap(res => {
              const file = b64toBlob(res?.file || '', 'application/pdf');
              FileSaver.saveAs(file, res?.name);
            }),
            catchError(() => {
              this.modalSvc.closeWithErrorMsg(this.initialData.modalComponentData.modalName);

              return EMPTY;
            })
          );
        }),
        tap(() => {
          this.modalSvc.closeWithSuccessMsg(this.initialData.modalComponentData.modalName);
        })
      )
      .subscribe();
  }

  private initForm(): void {
    this.certificateForm = new FormGroup({
      certificate: new FormControl('', [Validators.required]),
      year: new FormControl(''),
      typeCertificate: new FormControl('')
    });

    this.onChangesForm();
  }

  private setInitialData(acceptButtonTitleDisabled: boolean) {
    this.initialData = {
      ...this.initialData,
      modalComponentData: {
        ...this.initialData.modalComponentData,
        acceptButtonTitleDisabled,
      },
    };
  }

  onChangesForm() {
    const typeCertificateForm = this.certificateForm?.get('typeCertificate');
    const yearControl = this.certificateForm?.get('year');

    this._subscriptions.sink = this.certificateForm.valueChanges.subscribe(value => {
      this.showYearField = value.certificate === Certificates.TAX_CERTIFICATE;
      this.showRactYearField = value.certificate === Certificates.RACT;

      if (yearControl) {
        if (value.certificate === Certificates.TAX_CERTIFICATE || value.certificate === Certificates.RACT) {
          yearControl.setValidators([Validators.required]);
        } else {
          yearControl?.setValue('', { emitEvent: false });
          yearControl.clearValidators();
          typeCertificateForm?.clearValidators();
          typeCertificateForm?.updateValueAndValidity({ emitEvent: false });
          this.showCertificationType = false;
        }
        yearControl.updateValueAndValidity({ emitEvent: false });
      }

      this.setInitialData(!this.certificateForm.valid);
      this.modalSvc.setModalData(this.initialData);
    });

    this._subscriptions.sink = this.certificateForm?.get('year')?.valueChanges.subscribe(value => {
      if (value !== '') {
        const selectedCertificate = this.certificateForm?.get('certificate')?.value;
        
        // Solo mostrar tipos de certificado para TAX_CERTIFICATE
        if (selectedCertificate === Certificates.TAX_CERTIFICATE) {
          const dataCertificates = this.availableCertificates?.filter(certificate => certificate.year === value);
          this.typesCertificate = dataCertificates ? dataCertificates.flatMap(cert => cert.certificates) : [];
          this.showCertificationType = value && this.typesCertificate.length > 0;
          this.certificateForm?.get('typeCertificate')?.setValue('');
          typeCertificateForm?.setValidators([Validators.required]);
          typeCertificateForm?.updateValueAndValidity({ emitEvent: false });
        } else {
          // Para RACT no hay tipos de certificado
          this.showCertificationType = false;
          typeCertificateForm?.clearValidators();
          typeCertificateForm?.updateValueAndValidity({ emitEvent: false });
        }
      } else {
        this.showCertificationType = false;
      }
    });
  }

  getmodalData() {
    this._subscriptions.sink = this.modalSvc.getModalData().subscribe({
      next: (data: IDynamicDialogConfig) => {
        this.initialData = data;
      },
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe();
  }
}
